import {
  faCloudDownload,
  faCog,
  faEdit,
  faEllipsisVertical,
  faMapLocation,
  faTrashCanCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { orderBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import IWContextMenu from 'shared/components/thunderbolt/IWContextMenu'
import IWContextMenuItem from 'shared/components/thunderbolt/IWContextMenuItem'
import IWContextDivider from 'shared/components/thunderbolt/IWContextMenuItemDivider'
import IWGeneralBadge from 'shared/components/thunderbolt/IWGeneralBadge'
import IWGenericTable from 'shared/components/thunderbolt/IWGenericTable'
import IWLoading from 'shared/components/thunderbolt/IWLoading'
import IWToggle from 'shared/components/thunderbolt/IWToggle'
import { downloadPfx } from 'shared/helpers'
import {
  deleteOrgCredential,
  getLsAdminCredentials,
  OrgCredential,
  toggleActiveOrgCredential,
} from 'shared/loadSchedulingClient'
import styled from 'styled-components'
import DeleteOrgCredentialModal from './DeleteOrgCredentialModal'
import LsAdminPageSectionHeader from './LsAdminPageSectionHeader'
import { lsAdminCredentialsTableSchema } from './tableSchemas'

const StyledBadgeDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

type TableItem = {
  market: string
  brand: string
  zone: string
  locationName: string
  location: string
  userId: string
  userAccount: string | null
  userSubAccount: string
  isSuma: string
  isConfigActive: JSX.Element
  enabled: JSX.Element
  contextMenu: JSX.Element
}

const tableColumns = lsAdminCredentialsTableSchema

const USE_QUERY_KEY = 'ls-admin-credentials'

export default function LsAdminCredentials() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [items, setItems] = useState<TableItem[]>([])
  const [isDeleteCredentialModalOpen, setIsDeleteCredentialModalOpen] =
    useState(false)
  const [currentViewableConfigId, setCurrentViewableConfigId] = useState<
    string | null
  >(null)
  const [credentialIdToDelete, setCredentialIdToDelete] = useState<
    string | undefined
  >(undefined)

  const { data: creds, isLoading } = useQuery({
    queryKey: [USE_QUERY_KEY, currentViewableConfigId],
    queryFn: async () => {
      return getLsAdminCredentials()
    },
  })

  const enableToggleMutation = useMutation({
    mutationKey: [USE_QUERY_KEY, 'toggle-enable'],
    mutationFn: ({
      credentialId,
      isActive,
    }: {
      credentialId: string
      isActive: boolean
    }) => {
      return toggleActiveOrgCredential(credentialId, isActive)
    },
    onSuccess: () => queryClient.invalidateQueries([USE_QUERY_KEY]),
  })

  const deleteCredentialMutation = useMutation({
    mutationKey: [USE_QUERY_KEY, 'delete'],
    mutationFn: (configId: string) => deleteOrgCredential(configId),
    onSuccess: () => queryClient.invalidateQueries([USE_QUERY_KEY]),
  })

  const mapper = (credentials: OrgCredential[]) => {
    let filtered = credentials || []

    if (currentViewableConfigId) {
      filtered = filtered.filter((c) => c.configId === currentViewableConfigId)
    }

    return orderBy(filtered, [
      'market',
      'brand',
      'zone',
      'locationName',
      'location',
    ]).map((item, index) => ({
      market: item.market,
      brand: item.brand,
      zone: item.zone,
      locationName: item.locationName,
      location: item.location,
      userId: item.userId,
      userAccount: item.userAccount,
      userSubAccount: item.userSubAccount,
      isSuma: item.isSuma ? t('common.yes') : t('common.no'),
      isConfigActive: (
        <StyledBadgeDiv>
          <IWGeneralBadge
            label={t(`common.${item?.isConfigActive ? 'active' : 'disable'}`)}
            color={item?.isConfigActive ? 'primary' : 'alert'}
          />
        </StyledBadgeDiv>
      ),
      enabled: (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <IWToggle
            id={index.toString()}
            checked={item.isActive}
            onChange={async () => {
              await enableToggleMutation.mutateAsync({
                credentialId: item.credentialId,
                isActive: !item.isActive,
              })
            }}
          />
        </div>
      ),
      contextMenu: (
        <IWContextMenu
          positions={['bottom', 'top']}
          menu={[
            <IWContextMenuItem
              as="routerLink"
              key={`ls-cred-view-configs-${item.credentialId}`}
              label={t('loadScheduling.contextMenus.viewConfigs')}
              icon={faCog}
              to={`/admin/load-scheduling/configs?configId=${item.configId}`}
            />,

            <IWContextMenuItem
              as="routerLink"
              key={`ls-config-view-la-${item.configId}`}
              data-testid={`ls-config-view-la-${item.configId}`}
              label={t('loadScheduling.contextMenus.viewLocationAttributes')}
              icon={faMapLocation}
              to={`/admin/load-scheduling/location-attributes?credentialId=${item.credentialId}`}
            />,

            <IWContextDivider />,

            <IWContextMenuItem
              as="button"
              key={`ls-cred-download-pfx-${item.credentialId}`}
              label={t('loadScheduling.contextMenus.downloadPFX')}
              icon={faCloudDownload}
              disabled={!item?.pfx}
              onClick={() => {
                if (!item?.pfx) {
                  return
                }

                const filename = `${item.market}-${item.brand}-${item.location}`
                downloadPfx(item.pfx, filename)
              }}
            />,

            <IWContextDivider />,

            <IWContextMenuItem
              as="routerLink"
              key={`ls-cred-edit-${item.credentialId}`}
              data-testid={`ls-cred-edit-${item.credentialId}`}
              label={t('button.edit')}
              icon={faEdit}
              to={`/admin/load-scheduling/credentials/edit/${item.credentialId}`}
            />,

            <IWContextMenuItem
              as="button"
              key={`ls-cred-delete-${item.credentialId}`}
              data-testid={`ls-cred-delete-${item.credentialId}`}
              label={t('button.delete')}
              icon={faTrashCanCheck}
              disabled={item.isActive}
              onClick={() => {
                setCredentialIdToDelete(item.credentialId)
                setIsDeleteCredentialModalOpen(true)
              }}
            />,
          ]}
        >
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </IWContextMenu>
      ),
    }))
  }

  useEffect(() => {
    const queryParamsConfigId = searchParams.get('configId')
    setCurrentViewableConfigId(queryParamsConfigId || '')
  }, [])

  useEffect(() => {
    if (creds?.length) {
      setItems(mapper(creds))
    }
  }, [creds])

  return (
    <main>
      <LsAdminPageSectionHeader
        header={t('loadScheduling.titles.adminCredentials')}
        desc={t('loadScheduling.descriptions.adminCredentials')}
        buttonLabel={t('loadScheduling.labels.credentials')}
        onButtonClick={() => {
          navigate('/admin/load-scheduling/credentials/create')
        }}
      >
        {isLoading && <IWLoading />}

        {Boolean(items?.length) && !isLoading && (
          <IWGenericTable
            border
            isLoading={isLoading}
            items={items}
            columns={tableColumns}
            showColumnSettings
            initialHiddenColumns={['isSuma']}
          />
        )}
      </LsAdminPageSectionHeader>

      <DeleteOrgCredentialModal
        isModalOpen={isDeleteCredentialModalOpen}
        credentialId={credentialIdToDelete}
        onCancel={() => setIsDeleteCredentialModalOpen(false)}
        onDelete={async (credentialId?: string) => {
          if (!credentialId) {
            return
          }
          await deleteCredentialMutation.mutateAsync(credentialId)
          setIsDeleteCredentialModalOpen(false)
        }}
      />
    </main>
  )
}
